import { useState, useCallback } from 'react';
import { COPY_NOTIFICATION_TIMEOUT } from '../constants';

/**
 * Custom hook for managing message-related operations
 * @returns {Object} Message handling state and functions
 */
export const useMessageHandling = () => {
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [expandedTools, setExpandedTools] = useState({});

  /**
   * Copy text to clipboard and show notification
   * @param {string} text - The text to copy
   * @param {string} messageId - The ID of the message being copied
   */
  const copyToClipboard = useCallback((text, messageId) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text)
        .then(() => {
          setCopiedMessageId(messageId);
          setTimeout(() => setCopiedMessageId(null), COPY_NOTIFICATION_TIMEOUT);
        })
        .catch(err => {
          console.error('Failed to copy text:', err);
        });
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      try {
        document.execCommand('copy');
        setCopiedMessageId(messageId);
        setTimeout(() => setCopiedMessageId(null), COPY_NOTIFICATION_TIMEOUT);
      } catch (err) {
        console.error('Failed to copy text:', err);
      }
      
      document.body.removeChild(textArea);
    }
  }, []);

  /**
   * Toggle the expansion state of a tool message
   * @param {number} index - The index of the tool message
   */
  const toggleToolExpansion = useCallback((index) => {
    setExpandedTools(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  }, []);

  /**
   * Set multiple tool expansions at once
   * @param {Object} expansions - Object with index keys and boolean values
   */
  const setMultipleToolExpansions = useCallback((expansions) => {
    setExpandedTools(prev => ({
      ...prev,
      ...expansions
    }));
  }, []);

  /**
   * Expand all tool messages
   */
  const expandAllTools = useCallback(() => {
    // This would need the total count of tool messages
    // Will be implemented when integrated with the main component
    console.warn('expandAllTools not yet implemented - needs message count');
  }, []);

  /**
   * Collapse all tool messages
   */
  const collapseAllTools = useCallback(() => {
    setExpandedTools({});
  }, []);

  /**
   * Check if a specific tool is expanded
   * @param {number} index - The index of the tool message
   * @returns {boolean} Whether the tool is expanded
   */
  const isToolExpanded = useCallback((index) => {
    return expandedTools[index] || false;
  }, [expandedTools]);

  return {
    copiedMessageId,
    expandedTools,
    copyToClipboard,
    toggleToolExpansion,
    setMultipleToolExpansions,
    expandAllTools,
    collapseAllTools,
    isToolExpanded
  };
};